import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../provider/AuthContext';
import { useGetDevicesQuery } from '../provider/redux/query/device.service';
import { getNormalizedDevices, summarizeFleet } from '../utils/deviceUtils';
import { useSidebarSlot } from './SidebarSlotContext';
import { BranchIcon, CpuLoadIcon, HealthcheckIcon, LastSeenIcon, LastUpdaterIcon, MemoryIcon, SpikeUpIcon, TemperatureIcon } from './StatIcons';

const ListIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <rect x='3' y='4' width='8' height='11' rx='2'></rect>
    <rect x='3' y='17' width='8' height='4' rx='2'></rect>
    <rect x='13' y='4' width='8' height='6' rx='2'></rect>
    <rect x='13' y='12' width='8' height='9' rx='2'></rect>
  </svg>
);

const ClockRotateIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M3 2v6h6'></path>
    <path d='M3.51 9a9 9 0 1 0 .49-3'></path>
    <polyline points='12 7 12 12 15 15'></polyline>
  </svg>
);

const ChartIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <polyline points='3 17 9 11 13 15 21 7'></polyline>
    <circle cx='3' cy='17' r='1.5'></circle>
    <circle cx='9' cy='11' r='1.5'></circle>
    <circle cx='13' cy='15' r='1.5'></circle>
    <circle cx='21' cy='7' r='1.5'></circle>
  </svg>
);

function Sidebar() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { data, isFetching } = useGetDevicesQuery(undefined, {
    pollingInterval: 30000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: !isAuthenticated,
  });

  const devices = useMemo(() => getNormalizedDevices(data?.data?.devices), [data]);
  const stats = useMemo(() => summarizeFleet(devices), [devices]);
  const [stickyStats, setStickyStats] = useState(stats);
  const deviceMatch = location.pathname.match(/^\/device\/([^/]+)/);
  const deviceMatchId = deviceMatch?.[1] || null;
  const activeDevice = useMemo(() => (deviceMatchId ? devices.find(d => d.id === deviceMatchId) : null), [deviceMatchId, devices]);
  const [stickyDevice, setStickyDevice] = useState(null);
  const detailDevice = activeDevice || stickyDevice;
  const detail = detailDevice?.raw || null;
  const fleetActive = location.pathname === '/' || location.pathname.startsWith('/device');
  const { slot } = useSidebarSlot();

  useEffect(() => {
    if (!isFetching) {
      setStickyStats(stats);
    }
  }, [isFetching, stats]);

  useEffect(() => {
    if (!deviceMatchId) {
      setStickyDevice(null);
      return;
    }
    if (activeDevice?.id) {
      setStickyDevice(activeDevice);
      return;
    }
    setStickyDevice(prev => (prev?.id === deviceMatchId ? prev : null));
  }, [activeDevice, deviceMatchId]);

  const displayStats = isFetching && !devices.length ? stickyStats : stats;

  const formatDateTimeParts = value => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const pad2 = val => String(val).padStart(2, '0');
    const datePart = `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${pad2(date.getFullYear() % 100)}`;
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const timePart = `${pad2(hours)}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())} ${ampm}`;
    return { date: datePart, time: timePart };
  };

  const renderDateTime = value => {
    const parts = formatDateTimeParts(value);
    if (!parts) {
      return (
        <span className='stat-value'>
          <span className='stat-upper-text'>—</span>
        </span>
      );
    }
    return (
      <span className='stat-value'>
        <span className='stat-upper-text'>{parts.date}</span>
        <span className='stat-lower-text'> ({parts.time})</span>
      </span>
    );
  };

  const formatMemoryUsage = (freeBytes, totalBytes) => {
    const free = Number(freeBytes);
    const total = Number(totalBytes);
    if (!Number.isFinite(free) || !Number.isFinite(total) || total <= 0) return '—';
    const used = Math.max(total - free, 0);
    const gb = b => b / (1024 * 1024 * 1024);
    // Keep it compact: "used/total GB"
    return `${gb(used).toFixed(1)}/${gb(total).toFixed(1)} GB`;
  };

  const formatLoadAvg = value => {
    if (!value) return '—';
    const arr = Array.isArray(value) ? value : [];
    const nums = arr
      .slice(0, 3)
      .map(v => Number(v))
      .filter(v => Number.isFinite(v));
    if (!nums.length) return '—';
    return nums.map(v => v.toFixed(2)).join(' ');
  };

  const formatBranch = device => {
    const branch = device?.branch || '—';
    const sha = device?.sha ? device.sha.toString().trim().slice(0, 7) : '—';
    if (!sha) return branch;
    return (
      <span className='stat-value'>
        <span className='stat-upper-text'>{branch}</span>
        <span className='stat-lower-text'> ({sha})</span>
      </span>
    );
  };

  const getDeviceStatus = device => {
    const online = device?.connection === 'connected' && device?.probeStatus === 'up';
    return {
      label: online ? 'Online' : 'Offline',
      className: online ? 'up' : 'offline',
    };
  };

  const isLoadSpiked = value => {
    const arr = Array.isArray(value) ? value : [];
    const one = Number(arr[0]);
    const five = Number(arr[1]);
    if (!Number.isFinite(one) || !Number.isFinite(five)) return false;
    return one - five > 0.2;
  };

  return (
    <aside className='sidebar'>
      <nav className='side-nav'>
        <NavLink to='/' end className={() => `item${fleetActive ? ' active' : ''}`}>
          <ListIcon />
          Dashboard
        </NavLink>
        <NavLink to='/reports' className={({ isActive }) => `item${isActive ? ' active' : ''}`}>
          <ChartIcon />
          Report
        </NavLink>
        <NavLink to='/alerts' className={({ isActive }) => `item${isActive ? ' active' : ''}`}>
          <ClockRotateIcon />
          Alerts &amp; History
        </NavLink>
      </nav>

      {!detailDevice && displayStats.total > 1 && (
        <div className='fleet-stats'>
          <h5>Fleet Stats</h5>
          <div className='stat-row'>
            <span className='stat-label'>
              <span className='dot' style={{ background: '#5a4ef4' }}></span> Total Devices
            </span>
            <span>{displayStats.total}</span>
          </div>
          <div className='stat-row'>
            <span className='stat-label'>
              <span className='dot success'></span> Online
            </span>
            <span>{displayStats.online}</span>
          </div>
          <div className='stat-row'>
            <span className='stat-label'>
              <span className='dot danger'></span> Offline
            </span>
            <span>{displayStats.offline}</span>
          </div>
        </div>
      )}

      {slot && <div className='fleet-stats sidebar-slot'>{slot}</div>}

      {!slot && detailDevice && (
        <>
          <div className='fleet-stats'>
            <h5>System Info</h5>
            <div className='stat-row'>
              <span className='stat-label'>
                <span className={`dot ${getDeviceStatus(detailDevice).className === 'up' ? 'success' : 'danger'}`}></span> Status
              </span>
              <span className={`status-pill ${getDeviceStatus(detailDevice).className}`}>{getDeviceStatus(detailDevice).label}</span>
            </div>
            <div className='stat-row'>
              <span className='stat-label'>
                <LastSeenIcon className='stat-icon last-seen' /> Last Seen
              </span>
              {renderDateTime(detailDevice.lastSeen)}
            </div>
            <div className='stat-row'>
              <span className='stat-label'>
                <LastUpdaterIcon className='stat-icon updated-at' /> Updated At
              </span>
              {renderDateTime(detailDevice.lastUpdater)}
            </div>
            <div className='stat-row'>
              <span className='stat-label'>
                <HealthcheckIcon className='stat-icon healthchecked' /> Healthchecked
              </span>
              {renderDateTime(detailDevice.lastHealthcheck)}
            </div>
            <div className='stat-row'>
              <span className='stat-label'>
                <BranchIcon className='stat-icon branch' /> Branch
              </span>
              <span>{formatBranch(detailDevice)}</span>
            </div>
            <div className='stat-row'>
              <span className='stat-label'>
                <TemperatureIcon className='stat-icon temperature' /> Temperature
              </span>
              <span>{detailDevice.temperature != null ? `${Math.round(detailDevice.temperature)}°C` : '—'}</span>
            </div>
            <div className='stat-row'>
              <span className='stat-label'>
                <MemoryIcon className='stat-icon ram' /> RAM
              </span>
              <span>{formatMemoryUsage(detailDevice.freemem, detailDevice.totalmem)}</span>
            </div>
            <div className='stat-row'>
              <span className='stat-label'>
                <CpuLoadIcon className='stat-icon cpu-load' /> CPU Load
              </span>
              <span className='stat-inline'>
                {isLoadSpiked(detailDevice.loadAverage) && <SpikeUpIcon className='trend-icon spike' />}
                <span>{formatLoadAvg(detailDevice.loadAverage)}</span>
              </span>
            </div>
          </div>

          {detail?.criticalServices?.length > 0 && (
            <div className='fleet-stats'>
              <h5>Services</h5>
              {detail.criticalServices.map(service => (
                <div className='stat-row' key={service.name}>
                  <span className='stat-label'>{service.name}</span>
                  <span className={`status-pill ${service.status === 'up' ? 'up' : 'down'}`}>{service.status?.toUpperCase()}</span>
                </div>
              ))}
            </div>
          )}

          {detail?.timers?.length > 0 && (
            <div className='fleet-stats'>
              <h5>Timers</h5>
              {detail.timers.map(timer => (
                <div className='stat-row' key={timer.name}>
                  <span className='stat-label'>{timer.name}</span>
                  <span className={`status-pill ${timer.status === 'enabled' ? 'up' : 'down'}`}>{timer.status?.toUpperCase()}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

    </aside>
  );
}

export default Sidebar;
